import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Form, Radio, Table, TableColumn } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import moment from 'moment';
import { showTargetItems } from '../../constants';
import { getMetricsTableForDownloadTableList } from '@/pages/opinion/services/accounts';
import { dealwithNewOldData, initialUpperCase } from '@/pages/opinion/utils';
import { isSameTime } from '@/pages/opinion/utils/time';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import { reviewsFormat } from '@/pages/opinion/utils/value';
import CardCommon from '@/pages/opinion/components/CardCommon';
import './index.scss';
import ChannelLabel from '@/pages/opinion/components/ChannelLabel';
import { getLocale, useHistory, useIntl } from 'umi';
import { downloadAction } from '@/pages/opinion/components/DownloadCommon/download-page';
import { getTeaLocale } from '@/pages/home/utils/lang';
import { goStatsDetailAction } from '../../../stats/Detail';
import { openWindowUrl } from '@/pages/opinion/utils/url';

type MetricsTableForDownloadType = {
  type: 'channel' | 'account';
  keepParams: any;
};

type StatisticsType = {
  count_engagement?: number;
  count_follower?: number;
  count_likes?: number;
  count_comments?: number;
  count_shares?: number;
};

const { pageable, autotip, sortable, scrollable } = Table.addons;

const tableWidth = 200;
export default function MetricsTableForDownload({
  type = 'channel',
  keepParams = {},
}: MetricsTableForDownloadType) {
  const { formatMessage } = useIntl();
  const [radioValue, setRadioValue] = useState<'only' | 'DoD'>('DoD'); // 是否显示百分比
  const [showChart, setShowChart] = useState<boolean>(true); // 是否显示进度条
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<StatisticsType>({});
  const [sorts, setSorts] = useState<SortBy<any>>({
    by: 'followers',
    order: 'desc',
  });
  const [splitText, setSplitText] = useState('#_to_#');
  const [pageInfo, setPageInfo] = useState({
    pageSize: 10,
    page: 1,
  });

  const getParams = () => {
    const { channel, social_media_by_account, region, ...othersParams } =
      keepParams;
    return {
      model_type: type,
      sort_item: sorts.by,
      sort_model: sorts.order,
      page: pageInfo.page,
      page_size: pageInfo.pageSize,
      accounts: social_media_by_account,
      ...othersParams,
      channels: channel,
      regions: region,
    };
  };

  const fetchData = () => {
    setLoading(true);
    const params = getParams();
    getMetricsTableForDownloadTableList(params)
      .then((data: any) => {
        let {
          link_of_start_time,
          link_of_end_time,
          new_list,
          old_list,
          new_total,
          old_total,
          channel_account_split,
          max,
          total,
        } = data || {};
        setTotal(total || 0);
        const dataList = dealwithNewOldData(
          new_list,
          old_list,
          'name',
          showTargetItems,
          (_newItem: any, oldItem: any) => {
            let obj: any = {
              oldTimeList: [link_of_start_time, link_of_end_time],
              newTimeList: [keepParams.start_time, keepParams.end_time],
            };
            showTargetItems.forEach((item) => {
              obj[item + '_old'] = oldItem?.[item] || 0;
            });
            return obj;
          },
          max,
        );
        if (total > 0) {
          const totalList = dealwithNewOldData(
            [{ ...new_total, name: 'Total' }],
            [{ ...old_total, name: 'Total' }],
            'name',
            showTargetItems,
            (_newItem, oldItem) => {
              let obj: any = {
                oldTimeList: [link_of_start_time, link_of_end_time],
                newTimeList: [keepParams.start_time, keepParams.end_time],
              };
              showTargetItems.forEach((item) => {
                obj[item + '_old'] = oldItem?.[item] || 0;
              });
              return obj;
            },
            max,
          );
          dataList.unshift(totalList[0]);
        }
        setSplitText(channel_account_split);
        setTableData(dataList || []);
        setStatistics(statistics);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [pageInfo, keepParams, type, sorts]);

  const showTextSuffix = useMemo(() => {
    return isSameTime('daily')(keepParams.start_time, keepParams.end_time)
      ? 'DoD'
      : 'Change%';
  }, [keepParams]);

  // TODO 这里的后面几个字段最好可以循环出来
  const tableColumns: TableColumn<any>[] = useMemo(() => {
    const showDoD = radioValue === 'DoD';

    return [
      {
        key: 'channel',
        header: formatMessage({ id: initialUpperCase(type) }),
        fixed: 'left',
        width: type === 'account' ? 250 : 160,
        render: ({ name: channel, account_url }) => {
          if (channel === 'Total') {
            return formatMessage({ id: 'Total' });
          }

          let nameText = undefined;
          if (type === 'account') {
            const [channelName, accountText] = channel.split(splitText);
            channel = channelName;
            nameText = accountText;
          }
          return (
            <a
              onClick={() => {
                type === 'account' && openWindowUrl(account_url);
              }}
            >
              <ChannelLabel
                channel_name={channel}
                replaceText={nameText}
              ></ChannelLabel>
            </a>
          );
        },
      },
      {
        key: 'followers',
        header: formatMessage({ id: 'Followers' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText={'followers'}
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
      {
        key: 'posts',
        header: formatMessage({ id: 'Posts-1' }),
        align: 'right',
        width: tableWidth,
        render: (item) => {
          return (
            <a
              onClick={() => {
                let name = item.name;
                let channelText = name;
                let nameText = '';
                if (type === 'account') {
                  const [channelName, accountText] = name.split(splitText);
                  channelText = channelName;
                  nameText = accountText;
                }
                jumpToFeeds(channelText, nameText, name === 'Total');
              }}
              style={{ width: '100%', textDecoration: 'none' }}
              className="dbo-accounts-table-link"
            >
              <TableItem
                keyText={'posts'}
                titleText="Posts-1"
                item={item}
                showTextSuffix={showTextSuffix}
                showDoD={showDoD}
                showChart={showChart}
              />
            </a>
          );
        },
      },
      {
        key: 'engagements',
        header: formatMessage({ id: 'Engagements' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText={'engagements'}
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
      {
        key: 'likes',
        header: formatMessage({ id: 'Likes' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText={'likes'}
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
      {
        key: 'comments',
        header: formatMessage({ id: 'Comments' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText="comments"
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
      {
        key: 'shares',
        header: formatMessage({ id: 'Share-2' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText="shares"
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
      {
        key: 'views',
        header: formatMessage({ id: 'Views' }),
        align: 'right',
        width: tableWidth,
        render: (item) => (
          <TableItem
            keyText="views"
            item={item}
            showTextSuffix={showTextSuffix}
            showDoD={showDoD}
            showChart={showChart}
          />
        ),
      },
    ];
  }, [
    showChart,
    radioValue,
    type,
    keepParams,
    showTextSuffix,
    formatMessage,
    splitText,
  ]);

  const downloadingTableInfo = () => {
    const params = getParams();
    downloadAction({
      limit: 150000,
      simpleName: type === 'account' ? 'ByAccount' : 'ByChannel',
      downLoadParams: {
        ...params,
        page: 1,
        page_size: 1000,
        split_text: splitText,
        downloadDOD: radioValue === 'DoD',
      },
      downloadFunc: 'getMetricsTableForDownloadTableList',
      sheetName: 'Account Metrics Table',
      total,
    });
  };

  const history = useHistory();
  const jumpToFeeds = (curChannel: any, account: string, isTotal: boolean) => {
    let params = {
      statsDetailType: 'feeds-ByChannelAndByAccount',
      ...keepParams,
      channel_social: isTotal ? [] : [curChannel],
      review_types: ['posts_text', 'posts_video'],
    };
    if (account) {
      params.social_media_by_account = [account];
    }
    goStatsDetailAction({
      path: `/opinion/Feeds/Feeds?tab=socialMedia`,
      params: params,
      history,
    });
  };

  const locale = getTeaLocale(getLocale());

  return (
    <div className="dbo-accounts-metricstable">
      <CardCommon
        title={
          <div className="overall-conext-title">
            {formatMessage({ id: 'Social Media Metrics Details' })}
          </div>
        }
        className="dbo-accounts-card overall-conext"
        hasDownload
        operations={[
          // 因为设计走查说不需要选中样式 所以把里面symbol: 'eye' 先删了
          {
            icon: showChart ? 'table-eye' : 'table-noeye',
            symbol: '',
            tip: formatMessage({
              id: 'Show comparison chart of column data. Comparison chart calculation method: Based on the maximum value of the column where the value resides,  use a bar chart to show the proportion of the value to the maximum value.',
            }),
            placement: 'top-end',
            placementOffset: [10, 10],
          },
        ]}
        handleOperationsOnClick={() => {
          setShowChart(!showChart);
        }}
        handleDownloadOnClick={downloadingTableInfo}
      >
        <>
          <Form.Item>
            <Radio.Group
              className="dbo-accounts-table-radio"
              value={radioValue}
              onChange={(value: any) => setRadioValue(value)}
            >
              <Radio name="only"> {formatMessage({ id: 'Value Only' })}</Radio>
              <Radio name="DoD">
                {formatMessage({ id: 'Show ' + showTextSuffix })}
              </Radio>
            </Radio.Group>
          </Form.Item>
          <ConfigProvider locale={locale}>
            <Table
              className="dbo-accounts-metric-table"
              bordered="all"
              columns={tableColumns}
              recordKey={({ name, followers }) => name + followers}
              records={tableData}
              addons={[
                pageable({
                  recordCount: total,
                  onPagingChange: (query) => {
                    let { pageIndex = 1, pageSize = 10 } = query;
                    if (pageSize !== pageInfo.pageSize) {
                      pageIndex = 1;
                    }
                    setPageInfo({ page: pageIndex, pageSize: pageSize });
                  },
                }),
                autotip({
                  isLoading: loading,
                }),
                sortable({
                  columns: showTargetItems.map((item) => ({
                    key: item,
                    prefer: 'desc',
                  })),
                  value: [sorts],
                  onChange: (value, { sort }) => {
                    let localSort = { ...sort };
                    if (!sort.order) {
                      localSort = { order: 'desc', by: 'followers' };
                    }
                    setSorts(localSort);
                  },
                }),
                scrollable({
                  minWidth: type === 'account' ? 1650 : 1560,
                }),
              ]}
            ></Table>
          </ConfigProvider>
        </>
      </CardCommon>
    </div>
  );
}

function TableItem({
  item,
  showDoD,
  showChart,
  keyText,
  showTextSuffix,
  titleText,
}: {
  item: any;
  showDoD: boolean;
  showChart: boolean;
  keyText: string;
  showTextSuffix: string;
  titleText?: string;
}) {
  const { formatMessage } = useIntl();
  titleText = titleText || keyText;
  const [value, percentageValue, chartPercentage, oldValue] = useMemo(() => {
    const percentageKey = keyText + '_than', // 环比key
      statisticsKey = keyText + '_progress'; // 占最大值百分比的key

    return [
      item[keyText],
      item[percentageKey],
      item[statisticsKey],
      item[keyText + '_old'],
    ];
  }, [item, keyText]);

  return (
    <div className={`${showChart ? 'is-percent' : 'is-no-percent'}`}>
      {/* 进度条，使用定位处理该内容 */}
      {item.name !== 'Total' && (
        <div
          className="table-text-bg"
          style={{
            width: showChart
              ? (chartPercentage > 100 ? 100 : chartPercentage) + '%'
              : 0,
          }}
        ></div>
      )}
      <div className="dbo-numtrends">
        <span className="dbo-numtrends-num">{value?.toLocaleString()}</span>
        <RingRatioBubble
          oldTime={item.oldTimeList}
          newTime={item.newTimeList}
          oldValue={oldValue}
          newValue={value}
          showContent={showDoD}
          formateDate={(key: moment.MomentInput) =>
            !key ? '-' : moment(key).format('YYYY-MM-DD')
          }
          formateValue={(key: any) => reviewsFormat(key, 2)}
          title={
            formatMessage({ id: initialUpperCase(titleText) }) +
            ' ' +
            formatMessage({ id: showTextSuffix })
          }
        >
          {showDoD ? (
            <PercentageItem percentage={percentageValue}></PercentageItem>
          ) : (
            ''
          )}
        </RingRatioBubble>
      </div>
    </div>
  );
}
